import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import { textColor, THEME_ALU } from '../../../../styles/basics/colors';
import { Button, ButtonSize, ButtonTheme } from '../../../../shared/common/Button';
import { useTranslation } from 'react-i18next';

const ButtonContainer = styled.div<{ disabled: boolean }>`
    display: inline-flex;
    white-space: nowrap;
    max-height: 32px;
    justify-content: flex-end;

    > * ~ * {
        margin-left: 10px;
    }

    .set-live-button {
        width: 150px;
    }
`;

const StSetLiveHint = styled.div`
    ${textColor(THEME_ALU)};
    font-size: 13px;
    text-align: center;
`;

interface Props {
    newState: boolean;
    disabled: boolean;
    updated: boolean;
    liveCollection: boolean;
    liveLibx: boolean;
    inactive: boolean;
    productId: string;
    shareLibx: boolean;
    isAssetUploaded: boolean;
    onDeactivateTitleClick?: () => void;
    onDeleteTitleClick?: () => void;
    onActivateTitleClick?: (productId: string) => void;
    onSetLiveClick?: () => void;
    onSendUpdatesClick?: (productId: string) => void;
    onSetLiveLibXchange?: () => void;
    onWithdrawLibXchange?: () => void;
}

const LCStatusActions: FC<Props> = props => {
    const { t } = useTranslation();
    const {
        newState,
        updated,
        inactive,
        liveLibx,
        isAssetUploaded,
        shareLibx,
        disabled = false,
        productId,
        onSendUpdatesClick = () => {},
        onSetLiveClick = () => {},
        onSetLiveLibXchange = () => {},
        onActivateTitleClick = () => {},
        onDeleteTitleClick = () => {},
        onDeactivateTitleClick = () => {},
        onWithdrawLibXchange = () => {}
    } = props;

    const isSendUpdatesShown = updated;
    const isSetLiveDisabled = disabled || (newState && !isAssetUploaded);
    const isSetLiveOnLibxDisabled = disabled || newState || shareLibx;
    const isActivateTitleShown = inactive;
    const isDeleteTitleShown = newState || inactive;
    const isDeactivateTitleShown = !isDeleteTitleShown;
    const isWithdrawLibxShown = liveLibx;

    const submitUpdatesBtn = isSendUpdatesShown ? (
        <Button
            onClick={() => onSendUpdatesClick(productId)}
            disabled={disabled}
            text={t('yourLocalContent.yourTitles.submitUpdates')}
            size={ButtonSize.LARGE}
        />
    ) : null;

    // show publish dialog
    let setLiveBtn: ReactNode = null;
    if (newState) {
        setLiveBtn = (
            <Button
                onClick={onSetLiveClick}
                disabled={isSetLiveDisabled}
                text={t('yourLocalContent.yourTitles.setLive')}
                size={ButtonSize.LARGE}
                className="set-live-button"
            />
        );

        if (newState && !isAssetUploaded) {
            setLiveBtn = (
                <div>
                    {setLiveBtn}
                    <StSetLiveHint>{t('yourLocalContent.yourTitles.warnings.noEbookUpload')}</StSetLiveHint>
                </div>
            );
        }
    }

    return (
        <ButtonContainer disabled={disabled}>
            {submitUpdatesBtn}
            {setLiveBtn}
            {isSetLiveOnLibxDisabled ? null : (
                <Button
                    onClick={onSetLiveLibXchange}
                    disabled={disabled}
                    text={t('yourLocalContent.yourTitles.libxChangeLive')}
                    size={ButtonSize.LARGE}
                />
            )}
            {isActivateTitleShown ? (
                <Button
                    onClick={() => onActivateTitleClick(props.productId)}
                    disabled={disabled}
                    text={t('yourLocalContent.yourTitles.activateTitle')}
                    size={ButtonSize.LARGE}
                />
            ) : null}
            {isDeleteTitleShown ? (
                <Button
                    onClick={onDeleteTitleClick}
                    disabled={disabled}
                    text={t('yourLocalContent.yourTitles.deleteTitle')}
                    size={ButtonSize.LARGE}
                    theme={ButtonTheme.NEUTRAL}
                />
            ) : null}
            {isDeactivateTitleShown ? (
                <Button
                    onClick={onDeactivateTitleClick}
                    disabled={disabled}
                    size={ButtonSize.LARGE}
                    theme={ButtonTheme.NEUTRAL}
                    text={t('yourLocalContent.yourTitles.deactivateTitle')}
                />
            ) : null}
            {isWithdrawLibxShown ? (
                <Button
                    onClick={onWithdrawLibXchange}
                    disabled={disabled}
                    size={ButtonSize.LARGE}
                    theme={ButtonTheme.NEUTRAL}
                    text={t('yourLocalContent.yourTitles.withdrawLibx')}
                />
            ) : null}
        </ButtonContainer>
    );
};

export default LCStatusActions;
